import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';

// The Licenses usage PDF (report type 'licenses-usage', Hugo Andrade 2026-10-30): how the tenant follows the license best
// practices, signed and dated like the other reports and listed on the Reports page too. The same request, poll and
// download steps as the Implementation footprint PDF. The signed URL is handed to `onOpen`; it is never logged or stored.
export const REPORT_TYPE = 'licenses-usage';
const MAX_POLLS = 90;
const OUTDATED = 'The report component needs an update. The next fleet push delivers it.';
const NOT_INSTALLED = 'The report component is not installed yet.';
const NO_DATA = 'There is no license data yet. The report can be created after the next collection.';
const FAILED = 'The PDF could not be created. Try again in a few minutes.';
const GENERIC = 'The PDF could not be prepared. Try again in a few minutes.';
const IN_PROGRESS = 'A report is already being prepared. Try again in a few minutes.';
const PROFILE_INCOMPLETE = 'The PDF needs a complete customer profile (company name, Microsoft tenant ID and primary domain). Ask IPC Global support to complete it.';
const ROLE_PDF = "Your role cannot create this PDF. The customer's admins and IPC Global operators create it; auditors read it on the Reports page.";
const SLOW = 'The PDF is taking longer than expected. Check the Reports page in a few minutes.';
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** The sentence for a refused or failed request (never the server's own text: it may name internals). */
export function reportRequestMessage(e) {
  const body = e?.body;
  if (e?.status === 409) {
    if (body?.error === 'report_function_outdated') return OUTDATED;
    if (body?.error === 'licenses_not_ready') return NO_DATA;
    const reason = body?.details?.reason;
    if (reason === 'report_not_ready') return NOT_INSTALLED;
    if (reason === 'customer_profile_incomplete') return PROFILE_INCOMPLETE;
    if (typeof body?.details?.reportId === 'string') return IN_PROGRESS;
    return GENERIC;
  }
  if (e?.status === 403) return ROLE_PDF;
  if (e?.status === 429) return 'The limit of reports per day was reached. Try again tomorrow.';
  return GENERIC;
}

/**
 * @param {{ customerId: string, onOpen?: (url: string) => void, pollMs?: number }} props
 */
export default function LicensesReportButton({ customerId, onOpen = (url) => window.location.assign(url), pollMs = 2000 }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const download = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { reportId } = await api.generateComplianceReport(customerId, { reportType: REPORT_TYPE });
      for (let n = 0; n < MAX_POLLS; n += 1) {
        if (!alive.current) return;
        const status = (await api.complianceReport(customerId, reportId))?.report?.status;
        if (status === 'succeeded') {
          const link = await api.complianceDownloadUrl(customerId, reportId, 'pdf');
          if (alive.current && typeof link?.url === 'string') onOpen(link.url);
          return;
        }
        if (status === 'failed') { if (alive.current) setMessage(FAILED); return; }
        await sleep(pollMs);
      }
      if (alive.current) setMessage(SLOW);
    } catch (e) {
      if (alive.current) setMessage(reportRequestMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [customerId, onOpen, pollMs]);

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button type="button" className="btn-secondary min-h-[40px]" disabled={busy} onClick={download} data-testid="licenses-report">
        {busy ? 'Preparing the PDF' : 'Licenses usage report (PDF)'}
      </button>
      {message ? <span role="alert" className="max-w-[22rem] text-[13px] text-ipc-ink">{message}</span> : null}
    </span>
  );
}
